import { LoadingIndicator } from '@vrplatform/components-legacy';
import { useAppName } from '@vrplatform/theme';
import { Anchor, Box, Center } from '@mantine/core';
import { Text } from '@mantine/core';
import { type ReactNode, useMemo, useState } from 'react';
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router';
import { Link } from 'react-router-dom';
import { Layout as OnboardingLayout } from '../../components/Layout';
import {
  type SignUpFormInputs,
  useSignInForm,
  useSignInToken,
} from '../../hooks';
import { ResetPasswordView } from './ResetPasswordView';
import { ReturnToLoginLink } from './ReturnToLoginLink';
import { SecondFactorView } from './SecondFactorView';
import { SignIn } from './SignIn';
import { SignUp } from './SignUp';
import { SignUpConfirmationView } from './SignUpConfirmationView';

const SignInView = () => {
  const formProps = useSignInForm();
  const { tokenIsActive } = useSignInToken();

  const [teamType, setTeamType] = useState<SignUpFormInputs['type'] | null>(
    null
  );

  if (tokenIsActive) {
    return (
      <Center h="100vh">
        <LoadingIndicator isFullPageLoading size="md" />
      </Center>
    );
  }

  return (
    <Routes>
      <Route
        element={
          <LayoutWrapper
            {...formProps}
            allowSignUp={true}
            teamType={teamType}
          />
        }
      >
        {formProps.showSecondFactor ? (
          <Route path="*" element={<SecondFactorView {...formProps} />} />
        ) : (
          <>
            <Route
              path="reset-password"
              element={
                <ResetPasswordView
                  handlePasswordReset={formProps.handlePasswordReset}
                />
              }
            />
            <Route
              path="verify-email"
              element={
                <SignUpConfirmationView
                  handleResendConfirmation={() =>
                    formProps.handleResendVerification(undefined)
                  }
                  loadingResend={formProps.loading}
                  handleCodeConfirmation={formProps.handleCodeConfirmation}
                />
              }
            />
            <Route path="login" element={<SignIn {...formProps} />} />
            <Route
              path="sign-up"
              element={<SignUp {...formProps} setTeamType={setTeamType} />}
            />
            <Route
              path="choose-role"
              element={<SignUp {...formProps} setTeamType={setTeamType} />}
            />
            <Route path="/" element={<ReplaceOnRoot forceLogin={true} />} />
            <Route path="*" element={<ReplaceOnRoot forceLogin={true} />} />
          </>
        )}
      </Route>
    </Routes>
  );
};

export default SignInView;

const ReplaceOnRoot = ({ forceLogin }: { forceLogin?: boolean }) => {
  // const wasLoggedInBefore = useWasLoggedInBefore();

  // if (wasLoggedInBefore || forceLogin) return <Navigate to="/login" replace />;
  if (forceLogin) return <Navigate to="/login" replace />;

  return <Navigate to="/choose-role" replace />;
};

const LayoutWrapper = (
  formProps: ReturnType<typeof useSignInForm> & {
    allowSignUp: boolean;
    teamType: SignUpFormInputs['type'] | null;
  }
) => {
  const { tokenIsActive } = useSignInToken();
  const location = useLocation();

  const { appName } = useAppName();

  const currentType = (() => {
    if (formProps.showSecondFactor) return 'second-factor';
    if (formProps.showSignUpVerification) return 'verify-email';
    if (location.pathname === '/reset-password') return 'reset-password';
    if (location.pathname === '/sign-up') return 'sign-up';
    if (location.pathname === '/login') return 'sign-in';
    if (location.pathname === '/choose-role') return 'choose-role';
    return 'sign-up';
  })();

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const title = useMemo(() => {
    if (currentType === 'second-factor') return '2FA Authentication';
    if (currentType === 'reset-password') return 'Reset password';
    if (currentType === 'sign-up') return 'Create your account';
    if (currentType === 'choose-role') return `Sign up to ${appName}`;
    if (currentType === 'sign-in') return `Sign in to ${appName}`;
    if (currentType === 'verify-email') return 'Verify your email';
    return '';
  }, [
    currentType,
    appName,
    location.pathname,
    formProps.showSignUpVerification,
  ]);

  const footer = useMemo<Record<typeof currentType, ReactNode>>(() => {
    return {
      'sign-in': (
        <Text component="p" c="gray" size="xs" mt="auto">
          No account yet?{' '}
          {formProps.allowSignUp ? (
            <Anchor component={Link} to="/choose-role">
              Sign Up
            </Anchor>
          ) : (
            // biome-ignore lint/a11y/useValidAnchor: <explanation>
            <a href="#" className="vrtrust-request">
              Sign Up
            </a>
          )}
        </Text>
      ),
      'choose-role': <ReturnToLoginLink />,
      'reset-password': <ReturnToLoginLink />,
      'sign-up': <ReturnToLoginLink />,
      'second-factor': null,
      'verify-email': null,
    };
  }, [formProps.allowSignUp]);

  if (tokenIsActive) {
    return (
      <Center h="100vh">
        <LoadingIndicator isFullPageLoading size="md" />
      </Center>
    );
  }

  return (
    <OnboardingLayout
      title={title}
      GL={'no-billing'}
      teamType={formProps.teamType}
      sidebar={
        (currentType === 'sign-in' || currentType === 'reset-password') && (
          <Box
            sx={{
              height: '100%',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-end',
              alignItems: 'flex-start',
            }}
          >
            <Text c="white" mb="xs" size="md">
              "VRPlatform.app has transformed the way we handle our vacation
              rentals. The automated accounting features save us countless hours
              each month!"
            </Text>
            <Text c="white" opacity={0.5}>
              - Jamie L., Property Manager
            </Text>
          </Box>
        )
      }
      description={
        currentType === 'second-factor'
          ? 'Enter your 2FA code to get started with VRPlatform'
          : currentType === 'choose-role'
            ? 'Select your role to continue with the sign-up. This helps us tailor the experience to your needs.'
            : currentType === 'sign-up'
              ? 'Please provide your details to create your account and ensure secure access to all platform features.'
              : currentType === 'verify-email'
                ? 'Please check your email for the confirmation code. If you didn’t receive the email, we can resend it.'
                : 'Welcome back, please login with your credentials'
      }
      activeStep={
        currentType === 'sign-up'
          ? 'create-account'
          : currentType === 'choose-role'
            ? 'choose-role'
            : currentType === 'verify-email'
              ? 'verify-email'
              : undefined
      }
      footer={footer[currentType]}
      menu={null}
    >
      <Outlet />
    </OnboardingLayout>
  );
};
